'use client';

import React from 'react';
import ReactSelect, { Props as ReactSelectProps, GroupBase, StylesConfig } from 'react-select';
import { colors } from '@/constant/colors';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps<
  Option = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> extends Omit<ReactSelectProps<Option, IsMulti, Group>, 'styles'> {
  error?: string;
  label?: string;
  required?: boolean;
  styles?: ReactSelectProps<Option, IsMulti, Group>['styles'];
}

// 默认样式配置
const defaultStyles: StylesConfig<SelectOption, false, GroupBase<SelectOption>> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '42px',
    borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
    borderWidth: '1px',
    borderRadius: '0.375rem',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
    backgroundColor: 'white',
    '&:hover': {
      borderColor: state.isFocused ? '#6366f1' : '#9ca3af',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 12px',
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af',
    fontSize: '1rem',
    whiteSpaze: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '1rem',
    color: '#111827',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.375rem',
    marginTop: '4px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '4px 0',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? colors.themeColor 
      : state.isFocused 
        ? colors.themeColorLight 
        : 'white',
    color: state.isSelected ? 'white' : '#111827',
    fontSize: '1rem',
    padding: '8px 12px',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: state.isSelected ? colors.themeColorDark : colors.themeColorLight,
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? '#6366f1' : '#9ca3af',
    '&:hover': {
      color: '#6366f1',
    },
  }),
};

function Select<
  Option = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  label,
  error,
  required,
  styles,
  className = '',
  id,
  instanceId,
  ...props
}: SelectProps<Option, IsMulti, Group>) {
  // 生成唯一的 ID
  const generatedId = id || instanceId || `select-${Math.random().toString(36).substr(2, 9)}`;

  // 合并默认样式和自定义样式
  const mergedStyles = React.useMemo(() => {
    if (!styles) return defaultStyles as any;
    
    return Object.keys(defaultStyles).reduce((acc, key) => {
      const styleKey = key as keyof typeof defaultStyles;
      acc[styleKey] = (provided: any, state: any) => {
        const defaultStyle = (defaultStyles[styleKey] as any)(provided, state);
        const customStyle = styles[styleKey] ? (styles[styleKey] as any)(provided, state) : {};
        return { ...defaultStyle, ...customStyle };
      };
      return acc;
    }, {} as any);
  }, [styles]);

  return (
    <div className={`select-wrapper ${className}`}>
      {label && (
        <label 
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <ReactSelect
        {...props}
        instanceId={generatedId}
        styles={mergedStyles}
        className={`react-select-container ${error ? 'error' : ''}`}
        classNamePrefix="react-select"
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
      />
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
}

export default Select;